// ============================================
// PDF Preview Component
// Shows all generated PDFs with submit option
// ============================================

import React, { useState } from 'react';
import { GeneratedPDF, formsAPI } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

interface PDFPreviewProps {
  submissionId: string;
  pdfs: GeneratedPDF[];
  onSubmitSuccess: () => void;
  onSubmitFail?: () => void;
  onBack: () => void;
  onBehalfOf?: { userId: string | number; userName: string } | null;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api'
const PDFPreview: React.FC<PDFPreviewProps> = ({
  submissionId,
  pdfs,
  onSubmitSuccess,
  onSubmitFail,
  onBack,
  onBehalfOf,
}) => {
  const { t } = useLanguage();
  const [selectedPdf, setSelectedPdf] = useState<GeneratedPDF | null>(pdfs[0] || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await formsAPI.submitForms(submissionId, onBehalfOf || undefined);
      if (result.success) {
        onSubmitSuccess();
      } else {
        setSubmitError(t('preview.failedSave'));
        onSubmitFail?.();
      }
    } catch (error: any) {
      setSubmitError(error.response?.data?.message || t('preview.failedSave'));
      onSubmitFail?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPreviewUrl = (pdf: GeneratedPDF) => {
    return `${API_BASE_URL}/forms/preview/${submissionId}/${pdf.fileName}`;
  };

  const getDownloadUrl = (pdf: GeneratedPDF) => {
    return `${API_BASE_URL}/forms/download/${submissionId}/${pdf.fileName}`;
  };

  const handleDownload = async (pdf: GeneratedPDF) => {
    setDownloadingPdf(pdf.fileName);
    
    try {
      // Get the download URL - always use backend endpoint for proper headers
      const downloadUrl = getDownloadUrl(pdf);
      
      // Create a temporary anchor element with download attribute
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = pdf.fileName; // Force download with filename
      a.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(a);
      a.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(a);
        setDownloadingPdf(null);
      }, 500);
      
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
      setDownloadingPdf(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-20 lg:pb-0">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('preview.title')}</h1>
              <p className="text-gray-500 text-xs sm:text-sm mt-1">
                {t('preview.reviewDocuments', { count: pdfs.length })}
              </p>
            </div>
            <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
              <button
                onClick={onBack}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                {t('preview.back')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 text-sm sm:text-base text-white rounded-lg font-medium ${
                  isSubmitting
                    ? 'bg-green-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isSubmitting ? t('preview.saving') : t('preview.submit')}
              </button>
            </div>
          </div>
          {submitError && (
            <div className="mt-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-sm sm:text-base text-red-700">
              {submitError}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
          {/* PDF List Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 border-b">
                <h2 className="text-sm sm:text-base font-semibold text-gray-900">{t('preview.generatedDocuments')}</h2>
              </div>
              <div className="divide-y max-h-96 lg:max-h-none overflow-y-auto">
                {pdfs.map((pdf, index) => (
                  <button
                    key={pdf.fileName}
                    onClick={() => setSelectedPdf(pdf)}
                    className={`w-full text-left p-3 sm:p-4 hover:bg-gray-50 transition-colors ${
                      selectedPdf?.fileName === pdf.fileName
                        ? 'bg-blue-50 border-l-4 border-blue-600'
                        : ''
                    }`}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                          {pdf.formName}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {t('preview.pages', { count: pdf.pageCount, plural: pdf.pageCount > 1 ? 's' : '' })}
                        </p>
                      </div>
                      <span className="flex-shrink-0 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        {t('preview.ready')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Download All Button */}
              <div className="p-3 sm:p-4 bg-gray-50 border-t">
                <div className="space-y-2">
                  {pdfs.map((pdf) => (
                    <button
                      key={pdf.fileName}
                      onClick={() => handleDownload(pdf)}
                      disabled={downloadingPdf === pdf.fileName}
                      className="w-full flex items-center justify-between p-2 text-xs sm:text-sm text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="truncate">{pdf.fileName}</span>
                      {downloadingPdf === pdf.fileName ? (
                        <svg className="animate-spin w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="flex-1">
            {selectedPdf ? (
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">{selectedPdf.formName}</h3>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <a
                      href={getPreviewUrl(selectedPdf)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs sm:text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="hidden sm:inline">{t('preview.openNewTab')}</span>
                      <span className="sm:hidden">{t('preview.open')}</span>
                    </a>
                    <button
                      onClick={() => handleDownload(selectedPdf)}
                      disabled={downloadingPdf === selectedPdf.fileName}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingPdf === selectedPdf.fileName ? (
                        <>
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {t('preview.downloading')}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          {t('preview.download')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="bg-gray-200" style={{ height: 'calc(100vh - 400px)', minHeight: '400px' }}>
                  <iframe
                    src={getPreviewUrl(selectedPdf)}
                    title={selectedPdf.formName}
                    className="w-full h-full"
                    style={{ border: 'none' }}
                    onError={() => console.log('iframe load error')}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                <p className="text-gray-500">{t('preview.selectPdf')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              {t('preview.pdfsGenerated', { count: pdfs.length })}
            </div>
            <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
              <button
                onClick={onBack}
                className="flex-1 sm:flex-none px-4 sm:px-6 py-2 text-sm sm:text-base text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                <span className="hidden sm:inline">{t('preview.goBackEdit')}</span>
                <span className="sm:hidden">{t('preview.edit')}</span>
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`flex-1 sm:flex-none px-4 sm:px-8 py-2 text-sm sm:text-base text-white rounded-lg font-medium flex items-center justify-center gap-2 ${
                  isSubmitting
                    ? 'bg-green-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="hidden sm:inline">{t('preview.savingDatabase')}</span>
                    <span className="sm:hidden">{t('preview.saving')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="hidden sm:inline">{t('preview.submitSaveAll')}</span>
                    <span className="sm:hidden">{t('preview.submit')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPreview;

